import { createContext, useContext, useState, ReactNode } from "react";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import { cn } from "@/infrastructure/lib/utils";
import { useTranslation } from "react-i18next";

interface UploadContextType {
  showUploadProgress: (file: File) => void;
  updateProgress: (progress: number) => void;
  hideUploadProgress: () => void;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

export function UploadProvider({ children }: { children: ReactNode }) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState<File | null>(null);

  const showUploadProgress = (file: File) => {
    setCurrentFile(file);
    setUploadProgress(0);
    setIsUploading(true);
  };

  const updateProgress = (progress: number) => {
    setUploadProgress(progress);
  };

  const hideUploadProgress = () => {
    setIsUploading(false);
    setCurrentFile(null);
    setUploadProgress(0);
  };

  // Função para formatar o tamanho do arquivo
  const formatFileSize = (bytes: number) => {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  const { t } = useTranslation();

  return (
    <UploadContext.Provider value={{
      showUploadProgress,
      updateProgress,
      hideUploadProgress,
    }}>
      {children}

      <Dialog open={isUploading} modal>
        <DialogContent className="max-w-sm p-0 gap-0 overflow-hidden bg-background/95 backdrop-blur-sm border-none shadow-2xl">
          <div className="p-8 flex flex-col items-center gap-6">
            {/* Ícone de upload animado */}
            <div className="relative w-24 h-24">
              {/* Círculo de progresso */}
              <svg
                className="w-full h-full -rotate-90 transform"
                viewBox="0 0 100 100"
              >
                {/* Círculo base */}
                <circle
                  className="text-muted/20"
                  strokeWidth="4"
                  stroke="currentColor"
                  fill="none"
                  r="38"
                  cx="50"
                  cy="50"
                />
                {/* Círculo de progresso */}
                <circle
                  className={cn(
                    "transition-all duration-300 ease-in-out",
                    uploadProgress >= 100 ? "text-green-500" : "text-primary"
                  )}
                  strokeWidth="4"
                  strokeDasharray={238.76}
                  strokeDashoffset={238.76 - (uploadProgress / 100) * 238.76}
                  strokeLinecap="round"
                  stroke="currentColor"
                  fill="none"
                  r="38"
                  cx="50"
                  cy="50"
                />
              </svg>
              
              {/* Ícone de upload no centro */}
              <div 
                className={cn(
                  "absolute inset-0 flex items-center justify-center transition-transform duration-500",
                  uploadProgress >= 100 && "scale-110"
                )}
              >
                <svg
                  className={cn(
                    "w-8 h-8 transition-colors duration-300",
                    uploadProgress >= 100 ? "text-green-500" : "text-primary"
                  )}
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                >
                  {uploadProgress >= 100 ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z"
                    />
                  )}
                </svg>
              </div>
            </div>

            {/* Informações do arquivo e progresso */}
            <div className="space-y-1 text-center">
              <p className="text-sm font-medium">
                {uploadProgress >= 100 
                  ? t('knowledge.imports.upload.completed')
                  : t('knowledge.imports.upload.sending')}
              </p>
              {currentFile && (
                <p className="text-xs text-muted-foreground">
                  {currentFile.name} • {formatFileSize(currentFile.size)}
                </p>
              )}
            </div>

            {/* Porcentagem */}
            <div className={cn(
              "text-2xl font-semibold tabular-nums transition-colors duration-300",
              uploadProgress >= 100 ? "text-green-500" : "text-primary"
            )}>
              {uploadProgress}%
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </UploadContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUpload() {
  const context = useContext(UploadContext);
  if (context === undefined) {
    throw new Error("useUpload must be used within a UploadProvider");
  }
  return context;
}
